import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { ControlContainer, FormGroupDirective } from '@angular/forms';

/**
 * Numeric control used to display whole or fractional numeric values.
 *
 * Use this component to display weights, quantities, amounts, totals, etc
 */
@Component({
  selector: 'z-decimal',
  templateUrl: './decimal.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  viewProviders: [
    {
      provide: ControlContainer,
      useExisting: FormGroupDirective,
    },
  ],
})
export class DecimalComponent implements OnInit {
  /**
   * Unique identifier for this control.
   */
  @Input() id: string;

  /**
   * Works the same as FormControlName. This is the field in the form group that will be used for this component.
   */
  @Input() fieldName: string;

  /**
   * Label to display for this control. If omitted no label will be displayed. Defaults to `undefined`.
   */
  @Input() label?: string;

  /**
   * If defined this will display a hint below the control that can be used to assist users in determining what value to enter or select. Defaults to `undefined`.
   */
  @Input() hint?: string;

  /**
   * Placeholder text to display when there is no value. Defaults to `undefined`.
   */
  @Input() placeholder?: string;

  /**
   * If true any form validation errors will be displayed below the field (similar to a hint)
   */
  @Input() displayValidationErrors: boolean = true;

  /**
   * If true the field cannot be modified. Defaults to `false`.
   */
  @Input() readonly: boolean = false;

  /**
   * Input control size. Defaults to `undefined`.
   */
  @Input() size?: number;

  /**
   * Display a control that will clear any value set in this field. Defaults to `false`.
   */
  @Input() clearButton: boolean = false;

  @Input() matchTypes: boolean = false;

  /**
   * Control whether the value is right aligned or left aligned. Defaults to `true`.
   */
  @Input() alignRight: boolean = true;

  /**
   * When enabled numeric values are grouped using commas (1,000,000 versus 1000000). Defaults to `true`.
   */
  @Input() useGrouping: boolean = true;

  /**
   * Minimum number of fractional digits to display. Set this to `0` to disable display of fractional values (display whole values only). Defaults to `4`.
   */
  @Input() minFractionDigits: number = 4;

  /**
   * Maximum number of fractional digits to display. Set this to `0` to disable display of fractional values (display whole values only). Defaults to `4`.
   */
  @Input() maxFractionDigits: number = 4;

  /**
   * Minimum allowed value. Defaults to `undefined` which does not restrict the mimimum value.
   */
  @Input() min?: number;

  /**
   * Maximum allowed value. Defaults to `undefined` which does not restrict the maximum value.
   */
  @Input() max?: number;

  /**
   * Maximum length of the value entered. Defaults to `undefined`.
   */
  @Input() maxlength?: number;

  /**
   * When `true` this control will allow empty values to be used. Defaults to `true`.
   */
  @Input() allowEmpty: boolean = true;

  /**
   * Class to apply to this component. Defaults to `undefined`.
   */
  @Input() styleClass?: string;

  /**
   * Class to apply to the container for this component. Defaults to `undefined`.
   */
  @Input() containerStyleClass?: string;

  /**
   * Class to apply to the input control for this component. Defaults to `undefined`.
   */
  @Input() inputStyleClass?: string;

  /**
   * Emits when the clear button is clicked.
   */
  @Output() clear = new EventEmitter<void>();

  @Output() matchTypeChange = new EventEmitter<string>();

  constructor(private changeDetectorRef: ChangeDetectorRef, public formGroup: FormGroupDirective) {}

  ngOnInit() {
    if (!this.inputStyleClass) {
      this.inputStyleClass = `z-text-input`;
    } else {
      this.inputStyleClass = `${this.inputStyleClass} z-text-input`;
    }

    if (this.alignRight) {
      this.inputStyleClass = `${this.inputStyleClass} align-right`;
    }
  }

  update() {
    this.changeDetectorRef.markForCheck();
  }

  onClear(event: any) {
    this.clear.emit(event);
  }

  onMatchTypeChange(event: any) {
    this.matchTypeChange.emit(event);
  }
}
